import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { AlertCircle, ArrowUpCircle, Crown, History, Layers, Loader } from 'lucide-react';
import { modpackConfigsApiService } from '../../services/modpackConfigsApi';
import { ModpackConfig, ModpackConfigEnv, UNCLUSTERED } from '../../types/modpackConfig';
import type { FleetStatus } from '../../hooks/useFleetStatus';
import { liveFor } from '../../hooks/useFleetStatus';
import { SectionHeader } from '../ui/SectionHeader';
import { EmptyState } from '../ui/EmptyState';
import { ConfirmationModal } from '../ConfirmationModal';
import { UpdatePackModal } from './UpdatePackModal';
import { RecordWipeModal } from './RecordWipeModal';
import { LiveStatusBadge } from './LiveStatus';
import { LastWipe } from './ModpackConfigBadges';
import { lastWipe } from './configFields';

interface ClusterVersionPanelProps {
  config: ModpackConfig;
  members: ModpackConfig[];
  env: ModpackConfigEnv;
  canWrite: boolean;
  /** Live status per member, when the page has it. */
  fleet?: FleetStatus | null;
  onChanged: () => Promise<void> | void;
}

/**
 * A pack's cluster: the servers running it, one button to update them all,
 * and the ability to choose a different head.
 *
 * The head matters beyond bookkeeping — the Discord role and forum are keyed
 * on it, and it is the server /deletepack and the roles loop treat as the
 * cluster's identity. Changing it re-parents every member and moves the
 * Discord link along with it.
 */
export const ClusterVersionPanel = ({
  config, members, env, canWrite, fleet, onChanged,
}: ClusterVersionPanelProps) => {
  const [updating, setUpdating] = useState(false);
  const [wiping, setWiping] = useState(false);
  const [headCandidate, setHeadCandidate] = useState<ModpackConfig | null>(null);
  const [changingHead, setChangingHead] = useState(false);
  const [headError, setHeadError] = useState<string | null>(null);

  const standalone = !config.cluster_name || config.cluster_name === UNCLUSTERED;
  const versions = useMemo(
    () => [...new Set(members.map((m) => m.modpack_version ?? '—'))],
    [members],
  );
  const mixed = versions.length > 1;
  const head = members.find((m) => m.cluster_parent === m.id) ?? null;
  // The cluster's last wipe is the head's; members that disagree are flagged.
  const wipes = useMemo(() => [...new Set(members.map((m) => lastWipe(m)))], [members]);
  const wipeReference = head ?? config;

  if (config.cluster_parent === null) {
    return (
      <EmptyState
        icon={Layers}
        title="This server has no cluster"
        description="Set a cluster head on the Configuration tab to group it with the other servers running this pack."
      />
    );
  }

  const handleChangeHead = async () => {
    if (!headCandidate) return;
    setChangingHead(true);
    setHeadError(null);
    try {
      await modpackConfigsApiService.setClusterHead(
        config.cluster_parent as number, headCandidate.server_address, env,
      );
      setHeadCandidate(null);
      await onChanged();
    } catch (err) {
      setHeadError(err instanceof Error ? err.message : 'Failed to change the cluster head');
      setHeadCandidate(null);
    } finally {
      setChangingHead(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="subcard p-4">
        <SectionHeader
          title={standalone ? 'This server' : config.cluster_name || 'Cluster'}
          icon={Layers}
          count={members.length}
          description={
            standalone
              ? 'A standalone pack, so an update only affects this server.'
              : 'Every server running this pack. One update records the version on all of them and announces it once.'
          }
          actions={canWrite && (
            <div className="flex items-center gap-2">
              <button type="button" onClick={() => setWiping(true)} className="glass-button-secondary" title="Record when the world was last wiped, on every server here" data-tour="configs-wipe">
                <History className="w-4 h-4" />
                Record a wipe
              </button>
              <button type="button" onClick={() => setUpdating(true)} className="btn-primary">
                <ArrowUpCircle className="w-4 h-4" />
                Update pack
              </button>
            </div>
          )}
        />

        <p className="text-xs text-glass-400 mb-3 flex flex-wrap items-center gap-x-2">
          <span>World last wiped:</span>
          <LastWipe config={wipeReference} showNever prefix="" className="text-glass-200" />
          {lastWipe(wipeReference) !== null && (
            <span className="text-glass-500">— what players are told.</span>
          )}
          {wipes.length > 1 && (
            <span className="text-amber-300">Members disagree about the last wipe; recording one sets them all.</span>
          )}
        </p>

        {mixed && (
          <div className="alert alert-warning mb-3" role="status">
            <AlertCircle className="w-4 h-4 shrink-0" />
            <span>
              These servers are not all on the same version ({versions.join(', ')}). That usually
              means a previous update did not finish — running the update again is safe.
            </span>
          </div>
        )}

        {headError && (
          <div className="alert alert-danger mb-3" role="alert">
            <AlertCircle className="w-4 h-4 shrink-0" />
            <span>{headError}</span>
          </div>
        )}

        <div className="divide-y divide-glass-200/10">
          {members.map((member) => {
            const isHead = member.cluster_parent === member.id;
            return (
              <div key={member.id} className="flex items-center justify-between gap-3 py-3">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 min-w-0">
                    <Link
                      to={`/modpack-configs/${encodeURIComponent(member.server_address)}${env === 'development' ? '?env=development' : ''}`}
                      className="link font-mono text-sm truncate"
                    >
                      {member.server_address}
                    </Link>
                    {isHead && (
                      <span className="badge badge-purple shrink-0" title="The cluster's head: the Discord role and forum are keyed on this server">
                        <Crown className="w-3 h-3" />
                        Head
                      </span>
                    )}
                    {member.id === config.id && <span className="badge badge-neutral shrink-0">You are here</span>}
                  </div>
                  <p className="text-xs text-glass-400 flex flex-wrap items-center gap-x-2">
                    <span>{member.region_name || 'no region'}</span>
                    {fleet && <LiveStatusBadge live={liveFor(fleet, member.server_address)} withDetail />}
                    {wipes.length > 1 && <LastWipe config={member} showNever />}
                  </p>
                </div>
                <div className="flex items-center gap-3 shrink-0">
                  <span className="font-mono tabular-nums text-sm text-white">
                    {member.modpack_version || '—'}
                  </span>
                  {canWrite && !isHead && !standalone && (
                    <button
                      type="button"
                      onClick={() => setHeadCandidate(member)}
                      className="btn-xs glass-button-secondary"
                      title="Make this server the head of the cluster"
                    >
                      <Crown className="w-3 h-3" />
                      Make head
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {updating && (
        <UpdatePackModal
          config={config}
          members={members}
          env={env}
          fleet={fleet}
          onClose={() => setUpdating(false)}
          onUpdated={onChanged}
        />
      )}

      {wiping && (
        <RecordWipeModal
          config={config}
          members={members}
          env={env}
          onClose={() => setWiping(false)}
          onRecorded={onChanged}
        />
      )}

      {headCandidate && (
        <ConfirmationModal
          onClose={() => setHeadCandidate(null)}
          onConfirm={handleChangeHead}
          isLoading={changingHead}
          title={`Make ${headCandidate.server_address} the cluster head?`}
          message={
            `${head?.server_address ?? 'The current head'} steps down and every server in ${config.cluster_name} ` +
            `re-parents to ${headCandidate.server_address}. The Discord role and forum move with it. ` +
            `Nothing is deleted.`
          }
          confirmText="Change head"
        />
      )}

      {changingHead && !headCandidate && (
        <div className="flex items-center gap-2 text-xs text-glass-400">
          <Loader className="w-4 h-4 animate-spin" /> Changing head…
        </div>
      )}
    </div>
  );
};
